/**
 * **C04 · The year-end report to the directors** (Stream C, Documents.dc.html): page 1, laid out
 * by the `year_end_page` engine from Money's one-pager, the distribution plan as voted, the 8350
 * projects, the finance year, and the finance director from Roles. Scored against the drawing by
 * walk 99g.
 */
import type { ReactNode } from 'react';
import {
  FestivalClock,
  yearEndPage,
  yearFromOnePager,
  type YearEndStep,
} from '@cider/domain/engines';
import { useLoad, type Report } from '../views/money/shared.js';
import { usePrinted } from './printed.js';
import { DrawnPages } from './drawn-doc.js';
import { holder, type RoleRow } from './aod-package.js';

interface Plan {
  plan: { state: string; secondAt: string | null } | null;
  holdbacks: { id: string; kind: string }[];
  computed: { steps: { kind: string; label: string; cents: number; ref: string | null }[] };
  facts: { aodSetting: { bps: number | null } | null };
  /** When each step's bill was paid (M18); absent from an API that does not say yet. */
  payments?: { step: number; paidOn: string | null }[];
}
interface Project {
  name: string;
  amountCents: number;
  state: string;
  voteOn: string | null;
  startFiscalYear: number;
}
interface FinanceCard {
  steps: { obligation: string; due: string | null; state: string }[];
}

const VOTED = new Set(['two approvals', 'printed', 'reported']);

export function YearEndReport({ fy }: { fy: number }): ReactNode {
  const one = useLoad<Report>(`/money/reports/one-pager?fy=${String(fy)}`);
  const plan = useLoad<Plan>(`/money/distributions/${String(fy)}/plan`);
  const projects = useLoad<{ projects: Project[] }>('/money/projects');
  // The year's close lands in the next fiscal year's card (the AOD package, the distributions).
  const finance = useLoad<FinanceCard>(`/money/finance-year/${String(fy + 1)}`);
  const roles = useLoad<RoleRow[]>(`/admin/roles/year?year=${String(fy)}`);
  const loads = [one, plan, projects, finance, roles];
  const ready = loads.every((l) => l.data !== null || l.error !== null);
  usePrinted('doc-year-end', ready, { year: fy - 1 });
  if (!ready) return <p>Reading the figures…</p>;
  const p = plan.data;
  const kindOf = new Map((p?.holdbacks ?? []).map((h) => [h.id, h.kind]));
  const paid = new Map((p?.payments ?? []).map((x) => [x.step, x.paidOn]));
  const steps: YearEndStep[] = (p?.computed.steps ?? [])
    .map((s, i) => ({ s, i }))
    .filter(({ s }) => s.kind === 'holdback' || s.kind === 'share')
    .map(({ s, i }) => ({
      label: s.label,
      cents: s.cents,
      ...(paid.has(i) ? { paidOn: paid.get(i) ?? null } : {}),
      kind:
        s.kind === 'share'
          ? 'share'
          : kindOf.get(s.ref ?? '') === 'archdiocese'
            ? 'archdiocese'
            : 'held',
    }));
  const voted = p?.plan && VOTED.has(p.plan.state) && p.plan.secondAt ? p.plan.secondAt : null;
  const page = yearEndPage({
    fiscalYear: fy,
    day: FestivalClock.calendarDay(new Date()),
    year:
      one.data?.status === 'ok'
        ? yearFromOnePager(one.data.sections.find((s) => s.head === 'The year')?.rows ?? [])
        : null,
    plan: p?.plan
      ? {
          voted: voted ? FestivalClock.calendarDay(new Date(voted)) : null,
          steps,
          aodBps: p.facts.aodSetting?.bps ?? null,
        }
      : null,
    projects: projects.data ? projects.data.projects.filter((x) => x.startFiscalYear === fy) : null,
    finance: finance.data?.steps ?? null,
    financeDirector: holder(roles.data, 'finance_director'),
  });
  return <DrawnPages pages={[page]} />;
}
